import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import React, { useCallback, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  X,
} from "lucide-react";

/**
 * Premium product image gallery
 *
 * Features:
 *   - Main slider with prev/next arrows
 *   - Dot indicators
 *   - Clickable thumbnail strip
 *   - Full image visibility without cropping
 *   - Responsive image sizing
 *   - Expand / enlarged image view
 */
export default function ProductGallery({
  images = [],
  alt = "",
}) {
  const [active, setActive] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const swiperRef = useRef(null);

  const count = images.length;
  const activeImage = images[active];

  const goToSlide = useCallback((index) => {
    setActive(index);
    swiperRef.current?.slideTo(index);
  }, []);

  const goPrev = useCallback(() => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    } else {
      setActive((a) => (a - 1 + count) % count);
    }
  }, [count]);

  const goNext = useCallback(() => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    } else {
      setActive((a) => (a + 1) % count);
    }
  }, [count]);

  const closeExpanded = useCallback(() => {
    setIsExpanded(false);
  }, []);

  if (!count) return null;

  return (
    <>
      <div
        className="space-y-4 max-w-[590px] mx-auto lg:mx-0"
        data-testid="product-gallery"
      >
        {/* Main image */}
        <div
          className="
            relative
            w-full
            rounded-[2rem]
            overflow-hidden
            welljio-premium-card
            group
            select-none
            flex
            items-center
            justify-center
            bg-white
          "
          data-testid="gallery-main"
        >
          {/* Existing gallery background */}
          <div
            aria-hidden
            className="absolute inset-0 welljio-mesh pointer-events-none"
          />

          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            slidesPerView={1}
            spaceBetween={0}
            speed={500}
            onSlideChange={(swiper) => {
              setActive(swiper.activeIndex);
            }}
            className="w-full aspect-square"
          >
            {images.map((img, index) => (
              <SwiperSlide
                key={index}
                className="!h-full"
              >
                <div className="w-full h-full">
                  <img
                    src={img}
                    alt={alt}
                    className="
                      block
                      w-full
                      h-full
                      object-cover
                      object-center
                      cursor-zoom-in
                    "
                    draggable={false}
                    onClick={() => setIsExpanded(true)}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Expand button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(true);
            }}
            aria-label="Expand product image"
            data-testid="gallery-expand"
            className="
              absolute
              z-20
              bottom-4
              right-4
              h-11
              w-11
              rounded-full
              bg-white/90
              backdrop-blur-sm
              border
              border-black/5
              shadow-md
              flex
              items-center
              justify-center
              text-welljio-ink
              transition-all
              hover:bg-white
              hover:scale-105
            "
          >
            <Maximize2
              size={18}
              strokeWidth={1.8}
            />
          </button>

          {/* Previous / Next */}
          {count > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goPrev();
                }}
                aria-label="Previous image"
                data-testid="gallery-prev"
                className="
                  gallery-prev
                  absolute
                  z-20
                  left-4
                  top-1/2
                  -translate-y-1/2
                  h-11
                  w-11
                  rounded-full
                  bg-white/90
                  backdrop-blur-sm
                  border
                  border-black/5
                  shadow-md
                  flex
                  items-center
                  justify-center
                  text-welljio-ink
                  transition-all
                  hover:bg-white
                  hover:scale-105
                "
              >
                <ChevronLeft
                  size={20}
                  strokeWidth={2.2}
                />
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goNext();
                }}
                aria-label="Next image"
                data-testid="gallery-next"
                className="
                  gallery-next
                  absolute
                  z-20
                  right-4
                  top-1/2
                  -translate-y-1/2
                  h-11
                  w-11
                  rounded-full
                  bg-white/90
                  backdrop-blur-sm
                  border
                  border-black/5
                  shadow-md
                  flex
                  items-center
                  justify-center
                  text-welljio-ink
                  transition-all
                  hover:bg-white
                  hover:scale-105
                "
              >
                <ChevronRight
                  size={20}
                  strokeWidth={2.2}
                />
              </button>

              {/* Dot indicators */}
              <div className="absolute z-20 bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      goToSlide(i);
                    }}
                    aria-label={`Go to image ${i + 1}`}
                    className={`
                      h-1.5
                      rounded-full
                      transition-all
                      ${
                        active === i
                          ? "w-6 bg-welljio-ink"
                          : "w-1.5 bg-welljio-ink/30 hover:bg-welljio-ink/60"
                      }
                    `}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Thumbnails */}
        {count > 1 && (
          <div
            className="flex gap-3 overflow-x-auto pb-1"
            data-testid="gallery-thumbs"
          >
            {images.map((img, i) => (
              <button
                key={i}
                type="button"
                data-testid={`product-thumb-${i}`}
                onClick={() => goToSlide(i)}
                aria-label={`View image ${i + 1}`}
                className={`
                  shrink-0
                  w-20
                  h-20
                  rounded-2xl
                  overflow-hidden
                  border-2
                  transition-all
                  bg-welljio-tint
                  ${
                    active === i
                      ? "border-welljio-ink scale-[1.03]"
                      : "border-transparent hover:border-slate-200"
                  }
                `}
              >
                <img
                  src={img}
                  alt=""
                  className="
                    w-full
                    h-full
                    object-cover
                    object-center
                  "
                  draggable={false}
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Expanded image viewer */}
      {isExpanded && activeImage && (
        <div
          className="
            fixed
            inset-0
            z-[100]
            bg-black/90
            backdrop-blur-sm
            flex
            items-center
            justify-center
            p-4
            sm:p-8
          "
          role="dialog"
          aria-modal="true"
          aria-label="Expanded product image"
          onClick={closeExpanded}
        >
          {/* Close */}
          <button
            type="button"
            onClick={closeExpanded}
            aria-label="Close expanded image"
            data-testid="gallery-expand-close"
            className="
              absolute
              top-5
              right-5
              z-20
              h-11
              w-11
              rounded-full
              bg-white/90
              flex
              items-center
              justify-center
              text-welljio-ink
              shadow-lg
              hover:bg-white
            "
          >
            <X
              size={22}
              strokeWidth={2}
            />
          </button>

          {/* Expanded image */}
          <div
            className="
              relative
              max-w-[95vw]
              max-h-[92vh]
              flex
              items-center
              justify-center
            "
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={activeImage}
              alt={alt}
              className="
                max-w-[95vw]
                max-h-[92vh]
                w-auto
                h-auto
                object-contain
                object-center
                rounded-xl
              "
              draggable={false}
            />

            {/* Expanded previous */}
            {count > 1 && (
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous expanded image"
                className="
                  absolute
                  left-3
                  sm:-left-16
                  top-1/2
                  -translate-y-1/2
                  h-11
                  w-11
                  rounded-full
                  bg-white/90
                  flex
                  items-center
                  justify-center
                  text-welljio-ink
                  shadow-lg
                  hover:bg-white
                "
              >
                <ChevronLeft
                  size={22}
                  strokeWidth={2}
                />
              </button>
            )}

            {/* Expanded next */}
            {count > 1 && (
              <button
                type="button"
                onClick={goNext}
                aria-label="Next expanded image"
                className="
                  absolute
                  right-3
                  sm:-right-16
                  top-1/2
                  -translate-y-1/2
                  h-11
                  w-11
                  rounded-full
                  bg-white/90
                  flex
                  items-center
                  justify-center
                  text-welljio-ink
                  shadow-lg
                  hover:bg-white
                "
              >
                <ChevronRight
                  size={22}
                  strokeWidth={2}
                />
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}